// A quick look at the chosen workbook before it is uploaded, using the same
// rules the olink-proteomics pipeline applies when it reads it: a sheet named
// Data, one header row that starts DAid, barcode, Cohort, Diagnose followed by
// one uniquely named column per protein, and one sample row with at least one
// numeric protein value. The pipeline stays the authority; this only catches
// the common mistakes early, in the browser, without sending anything.

const METADATA_HEADERS = ['DAid', 'barcode', 'Cohort', 'Diagnose'];

function isNumeric(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return text !== '' && Number.isFinite(Number(text));
}

export async function checkWorkbook(file) {
  if (!/\.xlsx$/i.test(file.name)) {
    return { ok: false, error: 'Choose an Excel workbook that ends in .xlsx.' };
  }
  if (file.size === 0) return { ok: false, error: 'This file is empty.' };

  const XLSX = await import('xlsx');
  let workbook;
  try {
    workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  } catch (_error) {
    return { ok: false, error: 'This file could not be opened as an Excel workbook.' };
  }
  const sheet = workbook.Sheets.Data;
  if (!sheet) return { ok: false, error: 'The workbook needs a sheet named Data.' };

  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: null, raw: true });
  if (rows.length !== 2) {
    return {
      ok: false,
      error: `The Data sheet needs exactly one header row and one sample row. This one has ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}.`,
    };
  }
  const headers = rows[0].map((value) => (value === null || value === undefined ? '' : String(value).trim()));
  if (headers.length < 5 || METADATA_HEADERS.some((name, index) => headers[index] !== name)) {
    return {
      ok: false,
      error: 'The Data sheet must start with the columns DAid, barcode, Cohort and Diagnose, followed by one column per protein.',
    };
  }
  const proteins = headers.slice(4);
  if (proteins.some((name) => !name)) {
    return { ok: false, error: 'Every protein column needs a name in the header row.' };
  }
  if (new Set(proteins).size !== proteins.length) {
    return { ok: false, error: 'Each protein column name must be unique.' };
  }
  const values = rows[1].slice(4, headers.length);
  const measured = values.filter(isNumeric).length;
  if (measured === 0) return { ok: false, error: 'The sample row has no protein values.' };
  return { ok: true, proteinColumns: proteins.length, measured };
}
